<template>
  <div class="ui-filter popover-filter">
    <UiButton
      ref="mainBtn"
      :filled="!!filtersOpen || hasFilters"
      :disabled="disabled"
      icon="filter"
      @click="toggleFilters"
    >
      {{ $t('ui.filters.inner.filter') }}
    </UiButton>
    <Transition name="fade">
      <div
        v-show="filtersOpen"
        ref="popup"
        class="pop"
        @keyup.enter.stop
      >
        <div class="arrow" />
        <UiScrollbar
          wrap-style="max-height: 65vh; height: auto;"
          :native="false"
        >
          <div
            v-for="filter in filterConfig"
            :key="filter.key"
            class="filter-row"
            :class="{ disabled: filter.dependency && filter.dependency.some(key => !currentFilters[key]) }"
          >
            <span class="label">{{ $t(filter.label) }}</span>
            <UiSelectElement
              ref="select"
              :model-value="currentFilters[filter.model]"
              :placeholder="getPlaceholder(filter)"
              :filterable="filter.filterable"
              :clearable="filter.clearable"
              :disabled="filter.dependency && filter.dependency.some(key => !currentFilters[key])"
              :remote-method="filter.remoteMethod"
              :remote="filter.remote"
              :value-key="filter.optionKey"
              size="big"
              @visible-change="changeVisibility($event, filter.remoteMethod)"
              @update:model-value="filter.changeMethod
                ? filter.changeMethod($event, filter.model)
                : changeMethodDefault($event, filter.model)"
            >
              <template v-slot:options>
                <ElOption
                  v-for="item in filter.options"
                  :key="item[filter.optionKey]"
                  :label="item[filter.optionLabel]"
                  :value="filter.optionValue ? item[filter.optionValue] : item"
                />
              </template>
            </UiSelectElement>
          </div>
        </UiScrollbar>
        <div class="controls t:space-x-8">
          <UiButton
            type="negative"
            :disabled="disabled"
            icon="times"
            @click="resetFilters"
          >
            {{ $t('ui.table.reset') }}
          </UiButton>
          <UiButton
            filled
            :disabled="isDisabledGroup || disabled"
            icon="check"
            @click="applyFilters"
          >
            {{ $t('ui.table.apply') }}
          </UiButton>
        </div>
      </div>
    </Transition>
    <UiIcon
      v-if="!$_.isEmpty(modelValue)"
      :size="16"
      name="times-circle"
      color="var(--text-negative)"
      class="clear-search"
      :class="{ disabled }"
      @click="resetFilters"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import hash from 'object-hash';
import errorHandleMixin from '@/views/mixins/error-hadle';

const filterFn = (v) => v !== undefined && v !== '' && v !== null;

export default {
  name: 'CohortFilter',
  mixins: [
    errorHandleMixin,
  ],
  props: {
    modelValue: {
      type: Object,
      default() {
        return {};
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit', 'toggle'],
  data() {
    return {
      isDataLoading: false,
      currentFilters: {},
      brands: [],
      affiliateOptions: [],
      trafficSourceOptions: [],
      marketingMaterialOptions: [],
      offerOptions: [],
      campaignOptions: [],
      campaignRootOptions: [],
      filtersOpen: false,
      oldHash: null,
      newHash: null,
      filterValue: '',
    };
  },
  computed: {
    ...mapGetters({
      currentAcl: 'auth/currentAcl',
      adminAcl: 'auth/adminAcl',
    }),
    isViewAffiliate() {
      return (this.currentAcl.affiliates_view === 'allow') || this.adminAcl.is_superuser;
    },
    filterConfig() {
      return [
        {
          key: 'trafficType',
          label: 'reports.filters.traffic',
          model: 'traffic_type',
          options: this.trafficTypeOptions,
          optionKey: 'type',
          optionLabel: 'name',
          optionValue: 'type',
          filterable: false,
          clearable: true,
        },
        {
          key: 'players',
          label: 'reports.filters.players',
          model: 'players_filter',
          options: this.playersOptions,
          optionKey: 'type',
          optionLabel: 'name',
          optionValue: 'type',
          filterable: false,
          clearable: true,
        },
        {
          key: 'team',
          label: 'reports.filters.team',
          model: 'team_id',
          options: this.teamOptions,
          optionKey: 'id',
          optionLabel: 'name',
          optionValue: 'id',
          filterable: true,
          clearable: true,
        },
        {
          key: 'brand',
          label: 'reports.filters.brand',
          model: 'site_id',
          options: this.brands,
          optionKey: 'id',
          optionLabel: 'name',
          optionValue: 'id',
          filterable: true,
          clearable: true,
        },
        ...(this.isViewAffiliate ? [
          {
            key: 'affiliate',
            label: 'reports.filters.affiliate_id',
            model: 'affiliate',
            options: this.affiliateOptions,
            optionKey: 'id',
            optionLabel: 'email',
            filterable: true,
            clearable: true,
            remoteMethod: this.getAffiliates,
            remote: true,
            changeMethod: this.changeAffiliate,
          },
          {
            key: 'offer',
            label: 'reports.filters.offer',
            model: 'affiliateOffer',
            options: this.offerOptions,
            optionKey: 'id',
            optionLabel: 'name',
            filterable: true,
            clearable: true,
            remoteMethod: this.fetchOffers,
            remote: true,
            changeMethod: this.changeAffiliateOffer,
            disabled: true,
            dependency: ['affiliate_id'],
          },
          {
            key: 'trafficSource',
            label: 'reports.filters.traffic_source',
            model: 'trafficSource',
            options: this.trafficSourceOptions,
            optionKey: 'id',
            optionLabel: 'name',
            filterable: true,
            clearable: true,
            remoteMethod: this.fetchTrafficSources,
            remote: true,
            changeMethod: this.changeTrafficSource,
            dependency: ['affiliate_id'],
          },
        ] : []),
        {
          key: 'marketingMaterial',
          label: 'reports.filters.marketing_material',
          model: 'marketingMaterial',
          options: this.marketingMaterialOptions,
          optionKey: 'id',
          optionLabel: 'name',
          filterable: true,
          clearable: true,
          remoteMethod: this.fetchMarketingMaterial,
          remote: true,
          changeMethod: this.changeMarketingMaterial,
        },
        {
          key: 'mediaBuyingRootCampaign',
          label: 'reports.filters.media_buying_root_campaign',
          model: 'mediaBuyingCampaignRoot',
          options: this.campaignRootOptions,
          optionKey: 'id',
          optionLabel: 'name',
          filterable: true,
          clearable: true,
          remoteMethod: this.fetchRootCampaigns,
          remote: true,
          changeMethod: this.changeRootCampaign,
        },
        {
          key: 'mediaBuyingCampaign',
          label: 'reports.filters.media_buying_campaign',
          model: 'mediaBuyingCampaign',
          options: this.campaignOptions,
          optionKey: 'id',
          optionLabel: 'name',
          filterable: true,
          clearable: true,
          remoteMethod: this.fetchCampaigns,
          remote: true,
          changeMethod: this.changeCampaign,
          dependency: ['media_buying_root_campaign_id'],
        },
        {
          key: 'playersRegistration',
          label: 'reports.filters.players_registration',
          model: 'player_country_code',
          options: this.countries,
          optionKey: 'code',
          optionLabel: 'name',
          optionValue: 'code',
          filterable: true,
          clearable: true,
        },
      ];
    },
    teamOptions() {
      return this.adminAcl.teams;
    },
    playersOptions() {
      return [
        {
          type: '',
          name: this.$t('reports.players_type.all'),
        },
        {
          type: 'new',
          name: this.$t('reports.players_type.new'),
        },
        {
          type: 'old',
          name: this.$t('reports.players_type.old'),
        },
      ];
    },
    trafficTypeOptions() {
      return [
        {
          type: '',
          name: this.$t('ui.filters.select.all'),
        },
        {
          type: 'affiliates',
          name: this.$t('ui.filters.select.partners'),
        },
        {
          type: 'direct',
          name: this.$t('ui.filters.select.direct'),
        },
      ];
    },
    hasFilters() {
      return !this.$_.isEmpty(this.currentFilters);
    },
    isDisabledGroup() {
      return this.oldHash === this.newHash;
    },
    ...mapGetters('misc', ['countries']),
  },
  watch: {
    currentFilters: {
      deep: true,
      handler(value) {
        const data = this.$_.pickBy(value, filterFn);
        this.newHash = hash(data, { algorithm: 'md5' });
      },
    },
  },
  unmounted() {
    document.removeEventListener('click', this.clickOutside, true);
  },
  created() {
    this.currentFilters = this.$_.cloneDeep(this.modelValue);
  },
  mounted() {
    document.addEventListener('click', this.clickOutside, true);
    this.isDataLoading = true;

    Promise.all([
      ...(this.isViewAffiliate ? [
        this.getAffiliates('', { isInit: true }),
      ] : []),
      this.fetchBrands(),
      this.fetchMarketingMaterial('', { isInit: true }),
      this.fetchRootCampaigns('', { isInit: true }),
      ...(this.currentFilters.affiliate_id ? [
        this.fetchOffers('', { isInit: true }),
        this.fetchTrafficSources('', { isInit: true }),
      ] : []),
      ...(this.currentFilters.media_buying_root_campaign_id ? [
        this.fetchCampaigns('', { isInit: true }),
      ] : []),
    ]).finally(() => this.isDataLoading = false);
  },
  methods: {
    changeVisibility(val, remoteMethod) {
      if (!val && remoteMethod) {
        remoteMethod('', { isInit: true });
      }
    },
    updateCurrentFilters(key, val, { modelKey, ...additionalData } = {}) {
      this.currentFilters = {
        ...this.currentFilters,
        [key]: val?.id,
        ...additionalData,
      };

      if (!val) {
        this.currentFilters = this.$_.omit(this.currentFilters, [key, modelKey]);
      }
    },
    changeMethodDefault(value, key) {
      this.currentFilters = {
        ...this.currentFilters,
        [key]: value,
      };

      if (!value) {
        this.currentFilters = this.$_.omit(this.currentFilters, [key]);
      }
    },
    getPlaceholder(filter) {
      return filter.options?.length ? this.$t('ui.filters.select.all') : this.$t('reports.colunms_settings.no_data');
    },
    changeAffiliate(val) {
      this.updateCurrentFilters('affiliate_id', val, {
        affiliate: {
          id: val?.id,
          email: val?.email,
        },
      });

      this.currentFilters = this.$_.omit(this.currentFilters, ['affiliate_offer_id', 'traffic_source_id', 'affiliateOffer', 'trafficSource']);

      if (val?.id) {
        this.fetchOffers();
        this.fetchTrafficSources();
      } else {
        this.offerOptions = [];
        this.trafficSourceOptions = [];
        this.currentFilters = this.$_.omit(this.currentFilters, ['affiliate_id', 'affiliate']);
      }
    },
    changeAffiliateOffer(val, modelKey) {
      this.updateCurrentFilters('affiliate_offer_id', val, {
        modelKey,
        affiliateOffer: {
          id: val?.id,
          name: val?.name,
        },
      });
    },
    changeTrafficSource(val, modelKey) {
      this.updateCurrentFilters('traffic_source_id', val, {
        modelKey,
        trafficSource: {
          id: val?.id,
          name: val?.name,
        },
      });
    },
    changeMarketingMaterial(val, modelKey) {
      this.updateCurrentFilters('marketing_material_id', val, {
        modelKey,
        marketingMaterial: {
          id: val?.id,
          name: val?.name,
        },
      });
    },
    changeRootCampaign(val, modelKey) {
      this.updateCurrentFilters('media_buying_root_campaign_id', val, {
        modelKey,
        mediaBuyingCampaignRoot: {
          id: val?.id,
          name: val?.name,
          root_campaign_id: val?.root_campaign_id,
        },
      });

      this.currentFilters = this.$_.omit(this.currentFilters, ['media_buying_campaign_id', 'mediaBuyingCampaign']);
      this.campaignOptions = [];

      if (val) {
        this.fetchCampaigns();
      }
    },
    changeCampaign(val, modelKey) {
      this.updateCurrentFilters('media_buying_campaign_id', val, {
        modelKey,
        mediaBuyingCampaign: {
          id: val?.id,
          name: val?.name,
        },
      });
    },
    async fetchData(apiMethod, {
      currentFilterKey,
      query,
      optionsKey,
      isInit = false,
      additionalQuery = {},
    }) {
      let currentOptions = [];

      if (isInit && this.currentFilters[currentFilterKey]) {
        currentOptions = [this.currentFilters[currentFilterKey]];
      }

      const { data: { payload } } = await this.$api[apiMethod](query, additionalQuery);

      if (['mediaBuyingCampaignRoot', 'mediaBuyingCampaign'].includes(currentFilterKey)) {
        const mappedOptions = payload.map((el) => ({ ...el, name: `(ID ${el.incrementing_id}) ${el.name}` }));
        this[optionsKey] = this.$_.uniqBy([...currentOptions, ...mappedOptions], 'id');
      } else {
        this[optionsKey] = this.$_.uniqBy([...currentOptions, ...payload], 'id');
      }
    },
    async fetchRootCampaigns(search, { isInit } = {}) {
      await this.fetchData('getMediaBuyingCampaigns', {
        currentFilterKey: 'mediaBuyingCampaignRoot',
        optionsKey: 'campaignRootOptions',
        query: { search, root_campaign_id: 'null' },
        isInit,
      });
    },
    async fetchCampaigns(search, { isInit } = {}) {
      const { id } = this.currentFilters.mediaBuyingCampaignRoot;
      await this.fetchData('getMediaBuyingCampaigns', {
        currentFilterKey: 'mediaBuyingCampaign',
        optionsKey: 'campaignOptions',
        query: { search, root_campaign_id: id },
        isInit,
      });
    },
    async fetchBrands(search) {
      await this.fetchData('getSiteSettings', {
        currentFilterKey: 'brand',
        optionsKey: 'brands',
        query: { search },
      });
    },
    async fetchOffers(search, { isInit } = {}) {
      await this.fetchData('getAffiliateOffers', {
        currentFilterKey: 'affiliateOffer',
        optionsKey: 'offerOptions',
        query: this.currentFilters.affiliate_id,
        additionalQuery: { search },
        isInit,
      });
    },
    async fetchTrafficSources(search, { isInit } = {}) {
      await this.fetchData('getTrafficSources', {
        currentFilterKey: 'trafficSource',
        optionsKey: 'trafficSourceOptions',
        query: { search, affiliate_id: this.currentFilters.affiliate_id },
        isInit,
      });
    },
    async fetchMarketingMaterial(search, { isInit } = {}) {
      await this.fetchData('getMarketingMaterials', {
        currentFilterKey: 'marketingMaterial',
        optionsKey: 'marketingMaterialOptions',
        query: { search },
        isInit,
      });
    },
    async getAffiliates(search, { isInit } = {}) {
      await this.fetchData('getAffiliates', {
        currentFilterKey: 'affiliate',
        optionsKey: 'affiliateOptions',
        query: { search, limit: 100, offset: 0 },
        isInit,
      });
    },
    clickOutside(e) {
      if (!this.filtersOpen) return;

      if (this.$refs.mainBtn.$el?.contains(e.target)) return;

      const filters = [
        this.$refs.popup,
        ...this.$refs.select.map((ref) => ref.$refs.uiSelectElement.menuRef.$el),
      ].filter((el) => !!el);

      if (!filters.some((el) => e.target && el?.contains(e.target))) {
        this.toggleFilters();
      }
    },
    toggleFilters() {
      const oldVal = this.filtersOpen;
      this.filtersOpen = !this.filtersOpen;

      if (oldVal) {
        this.currentFilters = this.$_.cloneDeep(this.modelValue);

        const data = this.$_.pickBy(this.modelValue, filterFn);

        this.oldHash = hash(data, { algorithm: 'md5' });
      }

      this.$emit('toggle', this.filtersOpen);
    },
    applyFilters() {
      this.$emit('submit', this.currentFilters);
      this.filtersOpen = false;
    },
    resetFilters() {
      this.getAffiliates();
      this.fetchMarketingMaterial();
      this.currentFilters = {};
      this.$emit('submit', this.currentFilters);
      this.filtersOpen = false;
    },
  },
};
</script>

<style lang="scss">
.cohort-filter {
  max-width: 272px!important;
  box-sizing: border-box;

  .el-select-dropdown__item {
    //display: block!important;

    &.selected {
      color: inherit;
    }
  }
}
</style>
<style lang="scss" scoped>
.ui-filter {
  position: relative;
  line-height: 0;

  .pop {
    position: absolute;
    width: 576px;
    z-index: 9999;
    top: 44px;
    left: -350px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 16px;
    background-color: #fff;
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    user-select: none;

    &.fade-enter-active,
    &.fade-leave-active {
      transition: all 0.4s;
    }

    &.fade-enter,
    &.fade-leave-to {
      opacity: 0;
      transform: translateY(8px);
    }

    .arrow {
      position: absolute;
      top: -5px;
      left: 386px;
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-bottom: 5px solid #fff;
    }
    .filter-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 54px;
      border-bottom: 1px solid #eaeaea;

      > * {
        flex: 1;
      }

      > div {
        justify-content: flex-end;
      }

      &.disabled {
        pointer-events: none;

        .label {
          color: #e9ecef;
        }

        .select {
          opacity: 0.5;
        }
      }

      .label {
        font-size: 14px;
        font-weight: 500;
        color: #303634;
      }
    }
    .controls {
      display: inline-flex;
      justify-content: flex-end;
      flex-wrap: nowrap;
      width: auto;
      margin-top: 16px;
    }
  }

  // /deep/ {
    :deep(.el-input__inner) {
      height: 30px !important;
    }

    :deep(.el-input__suffix-inner){
      display: flex;
      height: 30px;
    }
    :deep(.el-select .el-input .el-select__caret){
      color: #7d7d7d;
      font-family: "Font Awesome 7 Pro" !important;

      &::before{
        position: relative;
        top: -1px;
        content: "\f106";
      }

      &.el-icon-circle-close{
        &:before {
          content: "\f057";
        }
      }

      &.is-reverse::before{
        top: -3px;
      }
    }
  // }
}

.clear-search{
  position: absolute;
  top: -8px;
  right: -8px;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  &.disabled {
    filter: grayscale(100%);
    pointer-events: none;
  }
}
</style>
